// web/src/components/parts.tsx — shared presentational parts for the reimagined
// console: sensitivity metadata + pill, a live urgency CountdownRing, and the
// segmented DurationPicker. Token-only styling (classes live in console.css).
// Authored in the 2026-06 UI reimagining. No data/wiring logic here.
import React from 'react';
import type { Sensitivity } from '../../../shared/types';

// --- Sensitivity ------------------------------------------------------------
export interface SensitivityMeta {
  label: string;
  /** CSS var driving the pill / cell accent colour. */
  colorVar: string;
  /** Cell hover-glow modifier class (empty for low/medium). */
  glowClass: string;
}

export const SENSITIVITY_META: Record<Sensitivity, SensitivityMeta> = {
  low: { label: 'low', colorVar: 'var(--color-sev-low)', glowClass: '' },
  medium: { label: 'medium', colorVar: 'var(--color-sev-medium)', glowClass: '' },
  high: { label: 'high', colorVar: 'var(--color-sev-high)', glowClass: 'bgCellHigh' },
  critical: { label: 'critical', colorVar: 'var(--color-sev-critical)', glowClass: 'bgCellCritical' },
};

export function SensitivityPill({ sensitivity }: { sensitivity: Sensitivity }): React.JSX.Element {
  const meta = SENSITIVITY_META[sensitivity];
  return (
    <span className="bgSev" style={{ color: meta.colorVar }}>
      <span className="bgSevDot" aria-hidden="true" />
      {meta.label}
    </span>
  );
}

// --- Countdown ring ---------------------------------------------------------
// The arc shows time remaining capped at a 1-hour reference (full ring = ≥1h
// left), depleting in real time; colour escalates as expiry nears. The server
// is authoritative on expiry (R2.4) — this is a live, honest cosmetic readout.
const RING_REF_MS = 60 * 60 * 1000;

export function formatRemaining(ms: number): { text: string; unit: string } {
  if (ms <= 0) return { text: '0:00', unit: 'expired' };
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return { text: `${h}:${String(m).padStart(2, '0')}`, unit: 'hrs left' };
  return { text: `${m}:${String(s).padStart(2, '0')}`, unit: 'remaining' };
}

export function CountdownRing({
  remainingMs,
  size = 48,
  big = false,
}: {
  remainingMs: number;
  size?: number;
  big?: boolean;
}): React.JSX.Element {
  const stroke = big ? 6 : 4;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const frac = Math.max(0, Math.min(1, remainingMs / RING_REF_MS));
  const secs = remainingMs / 1000;
  const arcClass =
    secs <= 0 ? 'bgRingArcRed' : secs <= 30 ? 'bgRingArcRed' : secs <= 120 ? 'bgRingArcAmber' : 'bgRingArcGreen';
  const { text, unit } = formatRemaining(remainingMs);
  return (
    <span className="bgRing" style={{ width: size, height: size }}>
      <svg className="bgRingSvg" width={size} height={size} aria-hidden="true">
        <circle className="bgRingTrack" cx={size / 2} cy={size / 2} r={r} strokeWidth={stroke} />
        <circle
          className={`bgRingArc ${arcClass}`}
          cx={size / 2}
          cy={size / 2}
          r={r}
          strokeWidth={stroke}
          strokeDasharray={circ}
          strokeDashoffset={circ * (1 - frac)}
        />
      </svg>
      <span className="bgRingLabel">
        <span className={big ? 'bgRingTimeBig' : 'bgRingTime'}>{text}</span>
        {big && <span className="bgRingUnit">{unit}</span>}
      </span>
    </span>
  );
}

// --- Duration picker --------------------------------------------------------
export interface DurationPreset {
  minutes: number;
  label: string;
}

export const DURATION_PRESETS: readonly DurationPreset[] = [
  { minutes: 15, label: '15m' },
  { minutes: 30, label: '30m' },
  { minutes: 60, label: '1h' },
  { minutes: 120, label: '2h' },
  { minutes: 240, label: '4h' },
] as const;

export function formatWindow(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
}

export function DurationPicker({
  minutes,
  max,
  onChange,
}: {
  minutes: number;
  max: number;
  onChange: (m: number) => void;
}): React.JSX.Element {
  const expiresAt = new Date(Date.now() + minutes * 60_000);
  return (
    <div>
      <div className="bgChips" role="group" aria-label="Access window presets">
        {DURATION_PRESETS.filter((p) => p.minutes <= max).map((p) => (
          <button
            key={p.minutes}
            type="button"
            className={`bgChip ${minutes === p.minutes ? 'bgChipActive' : ''}`}
            aria-pressed={minutes === p.minutes}
            onClick={() => onChange(p.minutes)}
          >
            {p.label}
          </button>
        ))}
      </div>
      <input
        className="bgRange"
        type="range"
        min={5}
        max={max}
        step={5}
        value={minutes}
        aria-label={`Access window: ${formatWindow(minutes)}`}
        onChange={(e) => onChange(Number(e.target.value))}
      />
      <div className="bgWindowOut">
        <span>{formatWindow(minutes)} window</span>
        <span className="bgWindowExp">
          expires ~
          {expiresAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>
    </div>
  );
}
